import React, { useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Container,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';

import {
  ContentContainer,
  getChoiceButtonStyle,
} from './AccessCodeSpark.styles';

import sparkTheme from '@/themes/sparkTheme';
import { SelectButton } from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';

const AccessCodeSparkInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();
  const history = useHistory();
  const { search } = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const choiceButtonStyle = getChoiceButtonStyle(theme);
  const program = getProgram(product);

  // Capture the redirectTo param passed by the Spark app
  // and persist it for use at the end of the flow in SmsVerify.
  // Clear any stale value when the param is absent so a previous
  // session's redirect cannot carry over into a new flow.
  useEffect(() => {
    const redirectTo = new URLSearchParams(search).get('redirectTo');
    if (redirectTo) {
      sessionStorage.setItem('spark_redirect_to', redirectTo);
    } else {
      sessionStorage.removeItem('spark_redirect_to');
    }
  }, [search]);

  useEffect(() => {
    posthog?.capture('SparkAccessCodeScreenViewed', { program });
  }, [posthog, product, program]);

  const handleYesClick = () => {
    posthog?.capture('SparkAccessCodeYesClicked', { program });
    history.push(`/activate/${product}/access-code/entry`);
  };

  const handleNoClick = () => {
    posthog?.capture('SparkAccessCodeNoClicked', { program });
    history.push(`/activate/${product}/employers`);
  };

  return (
    <ContentContainer>
      <Header />

      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.pre_onboarding.access_code.title')}
          </Typography>
          <Typography color="textPrimary">
            <Trans
              i18nKey="coverage.pre_onboarding.access_code.sub_title"
              components={{ bold: <strong /> }}
            />
          </Typography>
        </Box>

        <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleYesClick}
            data-testid="access-code-yes-button"
          >
            {t('coverage.pre_onboarding.access_code.yes')}
          </SelectButton>

          <SelectButton
            aria-pressed={false}
            style={choiceButtonStyle}
            onClick={handleNoClick}
            data-testid="access-code-no-button"
          >
            {t('coverage.pre_onboarding.access_code.no')}
          </SelectButton>
        </Box>
        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const AccessCodeSpark: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <AccessCodeSparkInner />
  </ThemeProvider>
);

export default AccessCodeSpark;
